import { ArrowRight, CheckCircle2, Clock3, ClipboardCheck, DollarSign } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { Job } from "@/lib/api";

interface PendingReviewsSectionProps {
  jobs: Job[];
  isLoading?: boolean;
  hasMore?: boolean;
}

export function PendingReviewsSection({
  jobs,
  isLoading = false,
  hasMore = false,
}: PendingReviewsSectionProps) {
  const countLabel = `${jobs.length}${hasMore ? "+" : ""}`;

  return (
    <section aria-labelledby="pending-reviews-heading">
      <Card className="overflow-hidden border-amber-400/25 bg-gradient-to-br from-amber-400/[0.08] via-surface-subtle/70 to-surface-subtle/50 shadow-lg shadow-amber-950/10">
        <CardHeader className="gap-4 border-b border-amber-400/15 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-400/20 bg-amber-400/10">
              <ClipboardCheck className="h-5 w-5 text-amber-300" />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle id="pending-reviews-heading" className="text-xl font-bold">
                  Pending reviews
                </CardTitle>
                {!isLoading && jobs.length > 0 && (
                  <Badge className="border border-amber-300/20 bg-amber-300/10 text-amber-200 hover:bg-amber-300/10">
                    {countLabel} awaiting decision
                  </Badge>
                )}
              </div>
              <CardDescription>
                Submitted work that needs your approval, revision request, or dispute decision.
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" asChild className="shrink-0 self-start text-xs hover:text-amber-200 md:self-auto">
            <Link to="/dashboard/jobs">
              View all jobs <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardHeader>

        <CardContent className="p-4 md:p-5">
          {isLoading ? (
            <div className="grid gap-3" aria-label="Loading pending reviews">
              {[1, 2].map((item) => (
                <Skeleton key={item} className="h-20 w-full rounded-xl bg-surface-subtle" />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-400/15 bg-emerald-400/[0.05] p-4">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" />
              <div>
                <p className="text-sm font-semibold">All caught up</p>
                <p className="text-xs text-muted-foreground">No submitted work is waiting for your review.</p>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              {jobs.map((job) => {
                const submittedAt = job.submittedAt ?? job.createdAt;

                return (
                  <article
                    key={job.id}
                    className="flex flex-col gap-4 rounded-xl border border-divider/60 bg-background/35 p-4 transition-colors hover:border-amber-400/25 hover:bg-background/50 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-bold md:text-base">{job.title}</h3>
                        <Badge variant="outline" className="border-cyan-400/20 bg-cyan-400/10 text-[9px] font-bold uppercase tracking-wider text-cyan-300">
                          In review
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono uppercase text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock3 className="h-3 w-3" />
                          Submitted {formatDistanceToNow(new Date(submittedAt), { addSuffix: true })}
                        </span>
                        <span className="flex items-center gap-1 text-primary/90">
                          <DollarSign className="h-3 w-3" />
                          {job.budgetUsdc} USDC
                        </span>
                        {job.agent?.name && <span>Agent: {job.agent.name}</span>}
                      </div>
                    </div>
                    <Button asChild size="sm" className="shrink-0 bg-amber-400 text-amber-950 hover:bg-amber-300">
                      <Link to={`/jobs/${job.id}`}>
                        Review work <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </article>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
